// ── 4대보험 요율 관리 섹션 ────────────────────────────────────────────────────
// PayrollSettings에서 분리 — 보험요율 조회/저장 (글로벌 설정, 회사 무관)

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { currentYearKST } from '../../../utils/date';
import { stdBlackBtn } from '../adminlayout/designStandard';

// ── 숫자 포맷 (3자리 콤마) ───────────────────────────────────────────────────
const fmt = (n) => n != null ? Number(n).toLocaleString('ko-KR') : '';
const parse = (s) => Number(String(s).replace(/,/g, '')) || 0;

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
const sectionTitle = { fontSize: 14, fontWeight: 800, color: 'var(--text-sub)', marginBottom: 12 };
const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)',
  fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
};
const labelStyle = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };
// 주요 CTA — 디자인 표준(stdBlackBtn)
const btnPrimary = { ...stdBlackBtn };

export default function InsuranceRatesSection({ showToast }) {
  const currentYear = currentYearKST();
  const [saving, setSaving] = useState(false);
  const [rateForm, setRateForm] = useState({
    year: currentYear, national_pension: '4.500', health_insurance: '3.545',
    long_term_care: '12.810', employment_ins: '0.900',
    national_pension_cap: '5,900,000', national_pension_floor: '390,000',
  });

  // ── 보험요율 로드 ──────────────────────────────────────────────────────────
  const fetchRates = useCallback(async () => {
    const res = await apiClient.get(`/api/admin/payroll/insurance-rates?year=${currentYear}`);
    if (res.ok && res.data?.data) {
      const r = res.data.data;
      setRateForm({
        year: r.year ?? currentYear,
        national_pension: String(r.national_pension ?? '4.500'),
        health_insurance: String(r.health_insurance ?? '3.545'),
        long_term_care: String(r.long_term_care ?? '12.810'),
        employment_ins: String(r.employment_ins ?? '0.900'),
        national_pension_cap: fmt(r.national_pension_cap),
        national_pension_floor: fmt(r.national_pension_floor),
      });
    }
  }, [currentYear]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  // ── 보험요율 저장 ──────────────────────────────────────────────────────────
  const saveRates = async () => {
    setSaving(true);
    const res = await apiClient.put('/api/admin/payroll/insurance-rates', {
      year: Number(rateForm.year),
      national_pension: Number(rateForm.national_pension),
      health_insurance: Number(rateForm.health_insurance),
      long_term_care: Number(rateForm.long_term_care),
      employment_ins: Number(rateForm.employment_ins),
      national_pension_cap: parse(rateForm.national_pension_cap) || null,
      national_pension_floor: parse(rateForm.national_pension_floor) || null,
    });
    setSaving(false);
    if (res.ok) showToast('보험요율이 저장되었습니다.', 'success');
    else showToast(res.data?.message || '저장 실패', 'error');
  };

  return (
    <div style={cardStyle}>
      <div style={sectionTitle}>4대보험 요율 ({rateForm.year}년)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>국민연금 근로자 부담률 (%)</label>
          <input style={inputStyle} value={rateForm.national_pension} onChange={(e) => setRateForm({ ...rateForm, national_pension: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>건강보험 근로자 부담률 (%)</label>
          <input style={inputStyle} value={rateForm.health_insurance} onChange={(e) => setRateForm({ ...rateForm, health_insurance: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>장기요양 건강보험료의 (%)</label>
          <input style={inputStyle} value={rateForm.long_term_care} onChange={(e) => setRateForm({ ...rateForm, long_term_care: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>고용보험 근로자 부담률 (%)</label>
          <input style={inputStyle} value={rateForm.employment_ins} onChange={(e) => setRateForm({ ...rateForm, employment_ins: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>국민연금 상한 기준소득월액</label>
          <input style={inputStyle} value={rateForm.national_pension_cap} onChange={(e) => setRateForm({ ...rateForm, national_pension_cap: e.target.value })} />
        </div>
        <div>
          <label style={labelStyle}>국민연금 하한 기준소득월액</label>
          <input style={inputStyle} value={rateForm.national_pension_floor} onChange={(e) => setRateForm({ ...rateForm, national_pension_floor: e.target.value })} />
        </div>
      </div>
      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <button style={btnPrimary} onClick={saveRates} disabled={saving}>
          {saving ? '저장 중...' : '요율 저장'}
        </button>
      </div>
    </div>
  );
}
